import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../lib/api';

export type SessionTier = 'free' | 'pro' | 'scale';

export type SessionSubscriptionStatus =
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'canceled'
  | 'unpaid'
  | 'incomplete'
  | 'incomplete_expired';

export interface SessionUser {
  id: string;
  email: string;
  name: string | null;
  /**
   * Avatar URL. Either a Google profile picture (auto-populated by Better
   * Auth on first OAuth sign-in) or a user-uploaded image stored in our R2
   * bucket. `null` means fall back to initials.
   */
  image: string | null;
  authMethod: 'cookie' | 'apikey';
  /** Admin flag from /api/me — gates access to the /admin SPA route. */
  isAdmin?: boolean;
  /** Effective billing tier — drives quota limits + UI gating. */
  tier: SessionTier;
  /** Raw Stripe subscription status (null = no subscription). */
  subscriptionStatus: SessionSubscriptionStatus | null;
  /** Unix seconds — when the current period ends. UI shows "renews on …". */
  currentPeriodEnd: number | null;
  /** True if the user cancelled in the portal (still active until period end). */
  cancelAtPeriodEnd: boolean;
  /** Has a Stripe Customer record — required to open the billing portal. */
  hasBillingCustomer: boolean;
  /** True when user has set up a brand profile — drives sidebar hints. */
  hasBrandProfile: boolean;
}

interface MeResponse {
  user: SessionUser | null;
}

export function useSession() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await apiFetch<MeResponse>('/api/me');
      setUser(data.user);
      setError(null);
    } catch (e) {
      setUser(null);
      setError(e instanceof Error ? e.message : 'unknown');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { user, loading, error, refresh };
}
